/**
 * Fotos de producto.
 *
 * The picture is how somebody at a counter finds the rice before they have finished reading
 * the word "arroz" — and, with three sizes of soda on the shelf, how they pick the right one.
 *
 * **The bytes never go in the log.** A photo is named by the SHA-256 of its bytes, and only
 * that name travels in an event (`producto.creado` / `producto.actualizado`, field `foto`).
 * The bytes go to `/api/fotos/<huella>` on their own. Three things follow from naming by
 * content rather than by id:
 *
 *   1. **Uploads are idempotent.** Sending the same photo twice stores it once, the same way
 *      a retried event is acknowledged rather than duplicated.
 *   2. **The server can check what it is given.** It hashes the upload and refuses anything
 *      whose bytes do not match the name, so no device can put different bytes behind a
 *      photo other devices already show.
 *   3. **A cached copy is never stale.** The same name always means the same bytes, so a
 *      device that has a photo never has to ask for it again.
 *
 * Kept small on purpose: a 320px square is sharp at the size a thumbnail is drawn, and at
 * 15–35 KB each a shop that photographs its whole catalogue fits on every phone it owns.
 */

import { z } from 'zod';

/** Side of the square every photo is reduced to, in pixels. */
export const FOTO_LADO = 320;

/**
 * The most the server will accept for one photo.
 *
 * Well above what the device pipeline produces — a 320px WebP is rarely past 35 KB — so this
 * only ever refuses something that did not come through it.
 */
export const FOTO_MAX_BYTES = 200_000;

/**
 * Raster formats only.
 *
 * SVG is left out on purpose: it is a document that can carry script, and a photo endpoint
 * that served one back would be a place to keep an attack. Nothing a camera produces is SVG.
 */
export const TIPOS_FOTO = ['image/webp', 'image/jpeg', 'image/png'] as const;
export type TipoFoto = (typeof TIPOS_FOTO)[number];

/** The name a photo travels under: the SHA-256 of its bytes, lowercase hex. */
export const fotoHashSchema = z.string().regex(/^[0-9a-f]{64}$/, 'Huella de foto inválida');

export const esHuellaFoto = (s: string): boolean => fotoHashSchema.safeParse(s).success;

/**
 * What the bytes actually are, from their first few, whatever anybody claims.
 *
 * The declared content type of an upload is the uploader's word; the magic number is the
 * file's. The server trusts only the second.
 */
export function tipoDeImagen(bytes: Uint8Array): TipoFoto | null {
  const b = (i: number) => bytes[i] ?? -1;
  if (b(0) === 0xff && b(1) === 0xd8 && b(2) === 0xff) return 'image/jpeg';
  if (
    b(0) === 0x89 && b(1) === 0x50 && b(2) === 0x4e && b(3) === 0x47 &&
    b(4) === 0x0d && b(5) === 0x0a && b(6) === 0x1a && b(7) === 0x0a
  ) {
    return 'image/png';
  }
  // RIFF....WEBP
  if (
    b(0) === 0x52 && b(1) === 0x49 && b(2) === 0x46 && b(3) === 0x46 &&
    b(8) === 0x57 && b(9) === 0x45 && b(10) === 0x42 && b(11) === 0x50
  ) {
    return 'image/webp';
  }
  return null;
}

/**
 * SHA-256 of the bytes, as lowercase hex.
 *
 * WebCrypto, so it is literally the same function on the phone that names the photo and on
 * the server that checks the name — the same reason the reducers are shared.
 */
export async function huellaFoto(bytes: Uint8Array | ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('WebCrypto no disponible');
  // A copy is always backed by a plain ArrayBuffer, which is what WebCrypto takes; a view over
  // shared memory is not, and the typings refuse it. At 35 KB the copy costs nothing.
  const datos = bytes instanceof Uint8Array ? bytes.slice() : new Uint8Array(bytes);
  const digest = await subtle.digest('SHA-256', datos);
  return [...new Uint8Array(digest)].map((x) => x.toString(16).padStart(2, '0')).join('');
}
